// app_client/src/store/useInvoiceStore.js
import { create } from 'zustand';
import axios from 'axios';
import { buildListQueryKey, normalizeListQueryParams } from '../hooks/useListQuery';

const createInitialInvoice = () => ({
  invoiceNumber: '',
  type: 'Sales',
  date: new Date().toISOString().split('T')[0],
  dueDate: new Date().toISOString().split('T')[0],
  accountId: '',
  accountName: '',
  operation: '',
  items: [],
  currency: 'TRY',
  exchangeRate: 1,
  notes: '',
  status: 'Active',
  paidAmount: 0,
  remainingAmount: 0,
  paymentStatus: 'Unpaid'
});

const useInvoiceStore = create((set, get) => ({
  invoices: [],
  loading: false,
  pagination: {
    totalItems: 0,
    currentPage: 1,
    totalPages: 1,
    limit: 10
  },
  lastParams: {}, 
  lastParamsKey: '',

  activeInvoice: createInitialInvoice(),
  resetForm: () => set({ activeInvoice: null }),
  clearCurrentInvoice: () => set({ activeInvoice: null }),
  resetActiveInvoice: () => set({ activeInvoice: createInitialInvoice() }),
  updateActiveInvoice: (data) => set((state) => ({
    activeInvoice: { ...state.activeInvoice, ...data }
  })),
  updateActiveInvoiceField: (field, value) => set((state) => ({
    activeInvoice: { ...state.activeInvoice, [field]: value }
  })),
  setActiveInvoiceItems: (items) => set((state) => ({
    activeInvoice: { ...state.activeInvoice, items }
  })),
  setTotals: (totals) => set((state) => ({
    activeInvoice: { ...state.activeInvoice, ...totals }
  })),

  // 1. Faturaları Listele (Pagination & Filter)
  fetchInvoices: async (params = {}, options = {}) => {
    const queryParams = normalizeListQueryParams({
      ...params,
      type: params.type === 'all' ? undefined : params.type
    }, params.page || 1);
    const queryKey = buildListQueryKey(queryParams, queryParams.page);
    const persistQuery = options.persistQuery !== false;
    const { loading, lastParamsKey } = get();

    if (loading && lastParamsKey === queryKey) {
      return;
    }

    set((state) => ({
      loading: true,
      ...(persistQuery ? {
        lastParams: queryParams,
        lastParamsKey: queryKey
      } : {
        lastParams: state.lastParams,
        lastParamsKey: state.lastParamsKey
      })
    }));
    try {
      const response = await axios.get('/api/invoices', { params: queryParams });
      
      // Backend'in { data: [], totalPages: 5, totalItems: 50 } formatında döndüğünü varsayıyoruz
      set({ 
        invoices: response.data.data,
        pagination: {
          totalItems: response.data.totalItems || response.data.total || 0,
          currentPage: response.data.currentPage || response.data.page || 1,
          totalPages: response.data.totalPages || 1,
          limit: queryParams.limit || 10
        },
        loading: false 
      });
    } catch (error) {
      console.error("Faturalar getirilirken hata oluştu:", error);
      set({ loading: false });
    }
  },

  // 2. Sayfa Değiştirme Yardımcısı
  setPage: (pageNumber) => {
    const currentParams = get().lastParams;
    get().fetchInvoices({ ...currentParams, page: pageNumber });
  },

  // 3. Tekil Fatura Detayı
  fetchInvoiceById: async (id) => {
    set({ loading: true });
    try {
      const response = await axios.get(`/api/invoices/${id}`);
      set({ loading: false });
      return response.data;
    } catch (error) {
      console.error("Fatura detayı alınamadı:", error);
      set({ loading: false });
      return null;
    }
  },

  // 4. Kaydet veya Güncelle
  saveInvoice: async (formData, id = null) => {
    set({ loading: true });
    try {
      let response;
      if (id) {
        response = await axios.put(`/api/invoices/${id}`, formData);
      } else {
        response = await axios.post('/api/invoices', formData);
      }
      
      set({ loading: false });
      // Kayıttan sonra mevcut sayfayı yenile
      await get().fetchInvoices(get().lastParams);
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.message || "Kayıt başarısız.";
      set({ loading: false });
      return { success: false, message };
    }
  },

  // 5. Durum Güncelleme
  updateInvoiceStatus: async (id, status) => {
    set({ loading: true });
    try {
      const response = await axios.patch(`/api/invoices/${id}/status`, { status });
      const updatedInvoice = response.data;
      
      set((state) => ({
        invoices: state.invoices.map((inv) => 
          inv._id === id ? { ...inv, ...updatedInvoice } : inv
        ),
        activeInvoice: state.activeInvoice?._id === id
          ? { ...state.activeInvoice, ...updatedInvoice }
          : state.activeInvoice,
        loading: false
      }));
      return { success: true, data: updatedInvoice };
    } catch (error) {
      set({ loading: false });
      return { success: false, message: error.response?.data?.message };
    }
  },

  cancelInvoice: async (id) => {
    set({ loading: true });
    try {
      const response = await axios.put(`/api/invoices/${id}/cancel`);
      const cancelledInvoice = response.data;

      set((state) => ({
        invoices: state.invoices.map((inv) => (
          inv._id === id ? { ...inv, ...cancelledInvoice } : inv
        )),
        activeInvoice: state.activeInvoice?._id === id
          ? { ...state.activeInvoice, ...cancelledInvoice }
          : state.activeInvoice,
        loading: false
      }));

      return { success: true, data: cancelledInvoice };
    } catch (error) {
      set({ loading: false });
      return {
        success: false,
        message: error.response?.data?.message || "Fatura iptal edilemedi."
      };
    }
  },

  fetchInvoiceDependencies: async (id) => {
    try {
      const response = await axios.get(`/api/invoices/${id}/dependencies`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || "Fatura bağlantıları alınamadı."
      };
    }
  },

  // 6. Silme
  deleteInvoice: async (id, reason = '') => {
    set({ loading: true });
    try {
      await axios.delete(`/api/invoices/${id}`, { data: { reason } });
      // Eğer sayfadaki son öğe silindiyse bir önceki sayfaya git
      const { pagination, invoices } = get();
      const currentPage = pagination.currentPage || 1;
      const nextPage = invoices.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;

      set({ loading: false });
      await get().fetchInvoices({ ...get().lastParams, page: nextPage });
      return { success: true };
    } catch (error) {
      set({ loading: false });
      return { success: false, message: error.response?.data?.message };
    }
  }
}));

export default useInvoiceStore;
